<template>
  <div class="flex flex-col h-full">
    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="store.filters.search"
        placeholder="Search requests..."
        :debounce-time="500"
        @search="handleSearch"
        class="w-full"
      />
    </Teleport>
    <!-- Header section -->
    <!-- Header section -->
    <DashboardHeader
      :title="`Referral Requests${store.totalCount > 0 && !store.loading ? ` (${store.totalCount} found)` : ''}`"
      link="/referral-requests"
    >
      <template #right />
    </DashboardHeader>

    <!-- Tabs -->
    <div class="mb-4">
      <div class="border-b border-gray-200 overflow-x-auto">
        <nav class="-mb-px flex space-x-6" aria-label="Tabs">
          <button
            v-for="tab in tabs"
            :key="tab.name"
            @click="selectTab(tab)"
            :class="[
              isTabActive(tab)
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
              'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center'
            ]"
          >
            <component :is="tab.icon" class="w-4 h-4 mr-2" />
            {{ tab.name }}
          </button>
        </nav>
      </div>
      <!-- Tab Description -->
      <div class="mt-2 text-sm text-gray-500">
        {{ currentTabDescription }}
      </div>
    </div>

    <!-- Table Component -->
    <ReferralRequestsTable
      @row-click="handleRowClick"
      @request-referral="handleRequestReferral"
    />

    <!-- Application Details Modal -->
    <JobApplicationModal
      v-if="selectedApplicationId"
      v-model:is-open="isModalOpen"
      :application-id="selectedApplicationId"
      :initial-tab="modalInitialTab"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { 
  UserCheck, 
  Clock, 
  UserX, 
  Ban, 
  AlertCircle, 
  Send, 
  LayoutList 
} from 'lucide-vue-next'
import SearchInput from '@/components/common/SearchInput.vue'
import { useReferralRequestsStore } from '@/stores/referralRequests'
import type { ReferralRequest } from '@/types/referralRequests'
import JobApplicationModal from '@/components/common/jobApplication/JobApplicationModal.vue'
import ReferralRequestsTable from '@/components/referrals/ReferralRequestsTable.vue'
import DashboardHeader from '@/components/common/DashboardHeader.vue'

const selectedApplicationId = ref<string | null>(null)
const isModalOpen = ref(false)
const modalInitialTab = ref<string | undefined>(undefined)
const router = useRouter()
const store = useReferralRequestsStore()

// Watch for route changes to open modal with correct request
watch(() => router.currentRoute.value.params.request_id, (newRequestId) => {
  if (newRequestId) {
    selectedApplicationId.value = String(newRequestId)
    isModalOpen.value = true
  }
})

// Watch isModalOpen to update URL when modal is closed
watch(isModalOpen, async (newVal) => {
  if (!newVal) {
    // Always refetch when modal closes to ensure data is up to date
    console.log('Modal closed - refetching data...')
    await store.fetchRequests({ page: store.pagination.currentPage })
    console.log('Data refetched successfully')
    
    selectedApplicationId.value = null
    modalInitialTab.value = undefined
    // Remove request_id from URL if modal is closed while preserving query params
    if (router.currentRoute.value.params.request_id) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push({
        name: 'Referral requests',
        query: router.currentRoute.value.query // Preserve existing query parameters
      })
    }
  }
})

const handleRowClick = (request: ReferralRequest) => {
  // Update URL with request ID while preserving query params
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  router.push({
    name: 'Referral request details',
    params: { request_id: String(request.id) },
    query: router.currentRoute.value.query // Preserve existing query parameters
  })
}

// Watch for route changes
watch(
  () => router.currentRoute.value,
  // eslint-disable-next-line complexity
  async (newRoute, oldRoute) => {
    // Skip if only the request_id param changed (added, removed, or modified)
    if (oldRoute) {
      const queryChanged = JSON.stringify(newRoute.query) !== JSON.stringify(oldRoute.query)
      
      // Get params without request_id for comparison
      const newParamsWithoutRequestId = { ...newRoute.params }
      delete newParamsWithoutRequestId.request_id
      const oldParamsWithoutRequestId = { ...oldRoute.params }
      delete oldParamsWithoutRequestId.request_id
      
      const otherParamsChanged = JSON.stringify(newParamsWithoutRequestId) !== JSON.stringify(oldParamsWithoutRequestId)
      
      // Only fetch if query params changed or other params changed (not just request_id)
      if (!queryChanged && !otherParamsChanged) {
        return // Skip fetch if only request_id changed
      }
    }

    // Default to Qualified if no relevant filters are present
    const hasFilters = newRoute.query.status || newRoute.query.ineligible || newRoute.query.unprocessed
    if (!hasFilters && !newRoute.query.search) {
       // If absolutely no filters (root visit), redirect to Qualified default
       // But we need to be careful not to infinite loop if we are already there?
       // The initFromUrl will handle it below, but we might want to push the route to reflect it?
       // Let's just default the store init if params are missing.
    }

    // Parse page from query or default to 1
    const page = typeof newRoute.query.page === 'string' ? parseInt(newRoute.query.page) : 1
    const search = typeof newRoute.query.search === 'string' ? newRoute.query.search : ''


    // Initialize store with URL params
    const statusParam = (newRoute.query.status as string) || ''
    const ineligibleParam = (newRoute.query.ineligible as string) || ''
    const unprocessedParam = (newRoute.query.unprocessed as string) || ''
    
    // Default to Qualified (APPROVED_FOR_REFERRAL) if no filters are present
    const isDefault = !statusParam && !ineligibleParam && !unprocessedParam && !search
    
    store.initFromUrl({
      page: String(page),
      search,
      status: isDefault ? 'APPROVED_FOR_REFERRAL' : statusParam,
      ineligible: ineligibleParam,
      unprocessed: unprocessedParam
    })

    // Fetch data with current state
    await store.fetchRequests({ page })
  },
  { immediate: true }
)

onMounted(async () => {
  // Check if there's a request_id in the route params and open the modal if present
  const requestId = router.currentRoute.value.params.request_id
  if (requestId) {
    selectedApplicationId.value = String(requestId)
    isModalOpen.value = true
  }
})

const handleSearch = async () => {
  await store.setSearch(router, store.filters.search)
}

const tabs = [
  { 
    name: 'Qualified for referrals', 
    filter: { status: 'APPROVED_FOR_REFERRAL' },
    description: 'Applications that passed AI job matcher and have status "Approved for referral"',
    icon: UserCheck
  },
  { 
    name: 'Need manual processing', 
    filter: { status: 'REFERRAL_PROCESSING_BY_OUTTALENT' },
    description: 'Applications that are currently in processing with status "Ready for referral"',
    icon: Clock
  },
  { 
    name: 'Pending', 
    filter: { status: 'READY_FOR_REFERRAL' },
    description: 'Applications that are currently in processing with status "Ready for referral"',
    icon: Clock
  },
  { 
    name: 'Ineligible referrals', 
    filter: { ineligible: true },
    description: 'Applications that have been given a low match score and are not qualified for referrals',
    icon: UserX
  },
  { 
    name: 'Rejected by us', 
    filter: { status: 'REFERRAL_REJECTED_BY_OUTTALENT' },
    description: 'Applications that have been rejected by OutTalent',
    icon: Ban
  },
  { 
    name: 'Unprocessed referrals', 
    filter: { unprocessed: true },
    description: 'Applications with job matching error',
    icon: AlertCircle
  },
  { 
    name: 'Submitted referrals', 
    filter: { status: 'OUTTALENT_REQUESTED_REFERRAL' },
    description: 'Applications that have been submitted to referring contacts by Outtalent',
    icon: Send
  },
  { 
    name: 'All', 
    filter: {},
    description: 'All referral requests',
    icon: LayoutList
  }
] 

import { computed } from 'vue'
const currentTabDescription = computed(() => {
  const activeTab = tabs.find(tab => isTabActive(tab))
  return activeTab ? activeTab.description : ''
})

const selectTab = async (tab: any) => {
  await store.setFilter(router, tab.filter)
}

const isTabActive = (tab: any) => {
  // Check if current store filters match the tab filter
  const f = tab.filter
  const sf = store.filters
  
  if (Object.keys(f).length === 0) {
    return !sf.status && !sf.ineligible && !sf.unprocessed
  }
  
  if (f.status) return sf.status === f.status
  if (f.ineligible) return sf.ineligible === true
  if (f.unprocessed) return sf.unprocessed === true
  
  return false
}

const handleRequestReferral = (request: ReferralRequest) => {
  selectedApplicationId.value = String(request.id)
  modalInitialTab.value = 'email_preview'
  isModalOpen.value = true
}
</script>
